/**
 * THE CHAT FLAG. The tab from `flag-button.tsx`, except that opening it gives you
 * the conversation in writing as well as in sound.
 *
 * This is a port of the reference prototype's `AgentWidget` — the flag launcher on
 * the edge of the page, and the chat window it slides open: header with a status
 * pill and a mute control, a scrolling transcript of both sides of the call, and a
 * composer at the bottom for a visitor who would rather type. The LOOK is meant to
 * be recognisable rather than improved, with one substitution that runs through
 * every colour in the file: the reference is Leidos purple and this is the house
 * yellow, so `accent`/`onAccent` default to the same #fac800 on #000000 that
 * `flag-button.tsx` defaults to. The two flags are one product and must read as one.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS DELIBERATELY DOES NOT PORT
 *
 * 1. THE PRESENTATION PANEL IS PORTED — FOR `show_info` CARDS ONLY (2026-08-26,
 *    reversing the 2026-08-25 decision this item used to record). The reference
 *    slides a second panel out beside the chat carrying five card kinds —
 *    `input_request` forms, `file` uploads, `completed`/`blueprint`/`submission`
 *    — each with a server contract (`submitCanvasInput`, `uploadFile`, export)
 *    that does not exist on `VoiceControl` and is still deliberately not built.
 *    What DOES exist is `show_info` screen cards — `VoiceCard` in `types.ts`, an
 *    optional title and a markdown-subset body, the whole of the contract — so
 *    the panel here carries those and nothing else: no forms, no attachments, no
 *    export button. See `PresentationPanel`. The panel slides out on the
 *    page-inner side of the chat window, and when the containing block has no
 *    room for two windows the cards fall back to rendering INLINE in the
 *    transcript (see `InfoCard` and the `cramped` computation), which is exactly
 *    how this variant drew every card before the panel existed.
 *
 * 2. NO PAGE SCRIM. The reference dims the whole page behind the widget, which is
 *    right for the settings screen it was built on and wrong on a stranger's
 *    marketing site: this thing is a chat window, not a modal, and greying out
 *    somebody's page because a visitor opened it is the behaviour that gets a
 *    widget uninstalled.
 *
 * 3. NO EXPORT, NO ATTACHMENTS. Both are reference features with no equivalent
 *    here — see the note above about the interface. The composer sends text.
 *
 * 4. IT MIRRORS. The reference is hard-coded to the right-hand edge. This takes
 *    `side` like the flag does, and mirroring is the same five-part job it is
 *    there: the rounded corners move, the label's rotation reverses, the panel
 *    slides out the other way, and the docked close tab ends up on the opposite
 *    face of the panel. (The tab docks to the panel's INNER edge — the face
 *    pointing into the page — which is what the reference does and is the only
 *    placement that leaves it reachable.)
 *
 * ---------------------------------------------------------------------------
 * WHY EVERY COLOUR AND DIMENSION HERE IS AN INLINE STYLE
 *
 * The same reason `flag-button.tsx` gives, and it applies harder to a widget with
 * this much surface area. This component is bundled into the embed and mounted
 * inside a closed shadow root on a page that is not ours. `:root` tokens and
 * compiled utility classes do not reach it, the embed's own stylesheet can fail to
 * load or be blocked by a CSP, and a chat panel that loses its stylesheet is not a
 * degraded chat panel — it is a column of unstyled text over somebody's homepage.
 * So there is not one class name in this file and nothing here depends on CSS
 * existing. That has three consequences worth stating, because each is a thing a
 * reader will otherwise try to "fix":
 *
 *   - FONT SIZES ARE px, NEVER rem. `rem` resolves against the HOST page's
 *     `<html>` font-size even inside a shadow root, and ServiceNow's Service
 *     Portal sets that to 10px.
 *   - THERE IS NO `::placeholder` RULE, so the composer's placeholder is a
 *     positioned span under a transparent textarea. Same trick, same reason, as
 *     `conversation-bar.tsx`.
 *   - THE TWO THINGS THAT LOOP — the streaming caret and the connecting dots —
 *     ARE DRIVEN BY THE WEB ANIMATIONS API, because `@keyframes` needs a
 *     stylesheet and `element.animate()` does not. See `useLoop`.
 *
 * TINTS ARE `currentColor` OVER A SURFACE, NEVER AN ALPHA OF IT. `accent` and
 * `surface` are arbitrary CSS colours — `oklch(...)`, `var(--x)`, a named colour —
 * so "the header colour at 12% opacity" is not something this file can compute.
 * Every tint is therefore an absolutely-positioned layer of `currentColor` at a
 * low opacity, which works against any colour the operator can type and needs no
 * parsing. The one exception is `surfaceBorder`, which is a field of its own for
 * exactly this reason (the conversation bar reached the same conclusion first).
 */
import {
  Fragment,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react'
import { renderMiniMarkdown, type MiniMarkdownImage, type MiniMarkdownStyles } from '@/lib/mini-markdown'
import { FlagBars } from './flag-bars'
import { MicGlyph, MicOffGlyph } from './mic-glyphs'
import { TAKEN_OVER_TEXT, connectingText, withTakeover } from './stage-text'
import {
  isLive,
  type VoiceCard,
  type VoiceControl,
  type VoicePhase,
  type VoiceStartStage,
  type VoiceToolCall,
  type VoiceTranscriptLine,
  type VoiceWidgetCommon,
} from './types'

/** The widget's own type, matching `.vai-root` in embed.css — inline, so the panel
 *  is typographically ours even when no stylesheet loaded at all. */
const FONT_STACK =
  "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif"

/** The house livery, kept to the hex rather than a token, and identical to
 *  `flag-button.tsx`'s: the two flags are one object with two behaviours. */
const ACCENT = '#fac800'
const ON_ACCENT = '#000000'

/**
 * The panel's own theme, and the reason it is three fields rather than a
 * light/dark switch. This widget paints a large surface; `surface` is that
 * surface, `onSurface` is the ink on it, and `surfaceBorder` is the hairline —
 * which cannot be derived from `onSurface` because an alpha of an arbitrary CSS
 * colour is not computable here. Same conclusion, same three fields, as the
 * conversation bar.
 */
const SURFACE = '#ffffff'
const ON_SURFACE = '#0a0a0a'
const SURFACE_BORDER = 'rgba(0, 0, 0, 0.12)'

/**
 * One red, for the failure box in the body.
 *
 * A single value rather than the flag's background/foreground pair because this
 * one is never a surface — it is text and a hairline over whatever `surface` is,
 * and #e5484d clears 4.5:1 on white and on near-black alike. The tinted fill
 * behind it is `currentColor`, so it follows the surface rather than the red.
 */
const DANGER = '#e5484d'

/** Collapsed tab, straight from the flag. */
const TAB_W = 40
const TAB_H = 160
/**
 * The tab's height once it has become a close button. The reference computes
 * `max(72, minLength * 0.6)`; 72 is what that produces for every launcher size it
 * ships, so it is stated rather than derived from a size table this file does not
 * have.
 */
const CLOSE_TAB_H = 72

/** The chat window. 380 x 560 is the reference's default widget box. */
const PANEL_W = 380
const PANEL_H = 560
/** Never narrower than this, whatever the viewport does. Below it the bubbles stop
 *  being bubbles and the composer stops holding a sentence. */
const PANEL_MIN_W = 260
/** The gap between the assembly and the two window edges it is not clamped to. */
const MARGIN = 16

/**
 * The presentation panel — the second window, on the page-inner side of the chat.
 * 340 is a card's comfortable reading width next to the 380 chat; 240 is the
 * reference's own hard floor, and it is a floor in the strong sense: a computed
 * width below it does not shrink the panel, it REMOVES it, and the cards render
 * inline in the transcript instead (see `cramped` in the geometry block). 10 is
 * the daylight between the two windows.
 */
const PRES_W = 340
const PRES_MIN_W = 240
const PRES_GAP = 10

/** The agent's avatar disc and the gap after it. Named because the tool cards
 *  indent by exactly this much to line up with the agent's bubbles. */
const AVATAR = 26
const AVATAR_GAP = 8

/** For the raw tool name only. Stated for the file's usual reason — there is no
 *  stylesheet here to carry a `mono` class — and `ui-monospace` first so it is the
 *  reader's own system mono rather than whatever the host page fell back to. */
const MONO_STACK =
  "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace"

/** Corner radius: 14 px on the tab (the flag's), 16 px on the panel. Only the two
 *  corners facing into the page get it on the tab; the panel is a free-floating
 *  card and gets all four. */
const R_TAB = 14
const R_PANEL = 16

/** The flag's stand-in for framer-motion's `{ stiffness: 300, damping: 30 }`, and
 *  the same 400 ms settle. Reproduced rather than imported: see flag-button.tsx. */
const SPRING = 'cubic-bezier(0.34, 1.4, 0.64, 1)'
const SPRING_MS = 400
const FADE_MS = 300

/**
 * The chat window's shadow — three faint layers rather than one heavy one: a
 * contact line, a mid lift and a wide soft one. The tab overlaps this panel's
 * edge, and a single dark layer is what turns that overlap into a grey band
 * instead of two objects.
 */
const PANEL_SHADOW = [
  '0 1px 2px rgba(0, 0, 0, 0.05)',
  '0 6px 16px rgba(0, 0, 0, 0.08)',
  '0 20px 48px rgba(0, 0, 0, 0.16)',
].join(', ')

/**
 * The presentation panel's shadow is DELIBERATELY NOT `PANEL_SHADOW`, and the
 * first cut's lesson is why (2026-08-27, first live ServiceNow run): the panel
 * lives inside a clipping wrapper — the clip IS the slide-out reveal — so a
 * shadow with 48px of blur was amputated at the wrapper's edges into hard
 * rectangular bands that read as a rendering bug, not a shadow. Two fixes
 * together: the wrapper now carries `PRES_BLEED` of transparent margin on every
 * side except the chat-facing one (which must keep clipping, it is the reveal),
 * and this shadow is tighter — negative spread pulls its extent inside that
 * bleed, so nothing is ever cut. Tighter is also RIGHT here, not a concession:
 * this is the assembly's secondary window, tucked behind the chat, and giving
 * the two identical elevation made them fight.
 */
const PRES_SHADOW = [
  '0 1px 2px rgba(0, 0, 0, 0.05)',
  '0 14px 36px -14px rgba(0, 0, 0, 0.3)',
].join(', ')

/** Transparent slack around the presentation panel inside its clipping wrapper,
 *  sized to PRES_SHADOW's worst reach (14px offset + 36px blur − 14px spread,
 *  with headroom). Pointer events pass through it — see the wrapper. */
const PRES_BLEED = 44

/** Very nearly the top of the int32 stack, with ~600 left above it — the flag's
 *  default and its reasoning. */
const DEFAULT_Z = 2147483000

/**
 * `theme` is omitted rather than accepted-and-ignored, which is the refusal the
 * flag and the conversation bar both make. The light/dark switch works by putting
 * `vai-dark`/`vai-light` on a wrapper for `embed.css`'s tokens to key off, and
 * there is not one class name in this file for those tokens to reach. The three
 * surface colours below ARE this widget's theme, and they are real.
 */
export interface ChatFlagProps extends Omit<VoiceWidgetCommon, 'theme'> {
  control: VoiceControl
  side?: 'left' | 'right'
  /** Where the CENTRE of the collapsed tab sits. A CSS length. */
  offsetY?: string
  /** Collapsed tab size. Default 40 x 160, the flag's. */
  width?: number
  height?: number
  /** The chat window. Clamped to the viewport at render — see `useViewportWidth`. */
  panelWidth?: number
  panelHeight?: number
  /**
   * The presentation panel that slides out beside the chat when the agent shows
   * a `show_info` card. Documented like `panelWidth`: a preferred width, clamped
   * to the room the containing block actually has. When the clamp lands below
   * 240 — a phone, a narrow preview card, or an operator who typed a value under
   * the floor — the panel is not rendered at all and cards go inline in the
   * transcript instead. Default 340.
   */
  presentationWidth?: number
  /** The panel's livery. See the note on SURFACE. */
  surface?: string
  onSurface?: string
  surfaceBorder?: string
  /**
   * The panel header's heading — the assistant's name, not the tab's copy.
   *
   * SEPARATE FROM `label` ON PURPOSE. `label` is the rotated word on a 40 px tab,
   * capped short and phrased as an instruction ("Click to Talk"); this is the name
   * of whoever answers, read at 14 px across a 380 px header. One string cannot be
   * both without one of the two reading like a mistake.
   */
  title?: string
  zIndex?: number
}

export function ChatFlag({
  control,
  side = 'left',
  offsetY = '50%',
  width = TAB_W,
  height = TAB_H,
  panelWidth = PANEL_W,
  panelHeight = PANEL_H,
  presentationWidth = PRES_W,
  surface = SURFACE,
  onSurface = ON_SURFACE,
  surfaceBorder = SURFACE_BORDER,
  zIndex = DEFAULT_Z,
  label = 'Click to Talk',
  endLabel = 'End call',
  title = 'Voice Assistant',
  accent = ACCENT,
  onAccent = ON_ACCENT,
  caption,
}: ChatFlagProps) {
  const [open, setOpen] = useState(false)
  const [ring, setRing] = useState(false)
  const [hover, setHover] = useState(false)
  /** The card picture being shown large, or null. One for the whole widget —
   *  see `Lightbox` and the context the cards reach it through. */
  const [lightbox, setLightbox] = useState<MiniMarkdownImage | null>(null)
  const closeLightbox = useCallback(() => setLightbox(null), [])
  /** The presentation panel, closed until a card arrives — see the auto-open
   *  effect below. Survives the chat closing: reopening the chat brings the
   *  panel back too, the same way the transcript survives. */
  const [presOpen, setPresOpen] = useState(false)
  const still = useMediaQuery('(prefers-reduced-motion: reduce)')

  const phase = control.phase
  const live = isLive(phase)

  /**
   * THE CLICK THAT OPENS THE PANEL IS ALSO THE CLICK THAT STARTS THE CALL, and it
   * has to be: a browser will only open a microphone inside a user gesture, and if
   * opening merely revealed a second "start" button the visitor would have to
   * press twice for one intention. The reference makes the same choice — its
   * widget starts the session in a mount effect, and the mount is the flag click.
   *
   * CLOSING STOPS THE CALL. The alternative — a call that keeps running behind a
   * closed panel — is a live microphone with no visible indicator on somebody
   * else's website, which is not a trade-off, it is a bug with a rationale.
   */
  const toggle = useCallback(() => {
    // NOT inside a `setOpen(prev => ...)` UPDATER, and the difference is not
    // stylistic: React runs an updater during the RENDER phase, so `control.start()`
    // in there lands a `setState` on whatever owns the call while this component is
    // still rendering — React 19 warns about exactly that, and in the playground it
    // is a cross-component update mid-render. The state this reads is one render
    // old only if two clicks land in the same tick, and both `start` and `stop` are
    // idempotent, so that costs nothing.
    if (open) control.stop()
    else control.start()
    setOpen(!open)
  }, [control, open])

  /**
   * A call that ends on its own leaves the panel OPEN, which is the one place this
   * disagrees with `flag-button.tsx` — and disagrees on purpose. There, the
   * expanded pill carries nothing but the meters, so a spent pill is litter and
   * collapsing is a kindness. Here the panel holds the transcript, and a
   * transcript that deletes itself the moment the agent hangs up is the widget
   * throwing away the only durable thing it produced. The body grows a restart
   * control instead, and the visitor closes it when they are done reading.
   */

  // --- geometry -------------------------------------------------------------

  /**
   * EVERY CLAMP HERE IS CSS ARITHMETIC, NOT JAVASCRIPT, AND THAT IS THE INTERESTING
   * DECISION IN THIS FILE.
   *
   * The obvious version measures `window.innerWidth` in a resize listener and does
   * the clamping in JS, which is what the reference does and what the first cut of
   * this file did. It is wrong in one specific place that matters more than it
   * sounds: THE PLAYGROUND PREVIEW. A `position: fixed` element whose ancestor has
   * `contain: paint` — which is exactly what the preview stage sets, so a floating
   * widget lands in the card instead of over the panel — is laid out against THAT
   * ANCESTOR, not against the viewport. `vw`, `100vh` and `window.innerWidth` all
   * keep reporting the window, so a 560 px panel rendered a card 440 px tall
   * overflowed it at both ends and the operator judged a widget whose header and
   * composer were sliced off.
   *
   * Percentages and `min()`/`max()` resolve against the containing block instead,
   * whichever one it is: the viewport on a customer's page, the card in the
   * preview. The same expression is correct in both, and the resize listener the
   * old version needed is gone with it.
   *
   * WHAT THE CLAMPS SAY. The panel gives up width first and never goes below
   * `PANEL_MIN_W` — below that the bubbles stop being bubbles. The tab never gives
   * up anything, because a tab thinner than a finger is not a control; it just
   * stops moving out once there is no room left to move into. On a 375 px phone
   * that lands at a 303 px panel with the tab beside it, which is where the
   * reference's own clamp lands.
   */
  const panelWCss = `max(${PANEL_MIN_W}px, min(${panelWidth}px, calc(100% - ${MARGIN * 2 + width}px)))`
  /** The chat and the presentation panel share one vertical geometry — one
   *  expression, so they cannot drift apart by a pixel of somebody's edit. */
  const panelHCss = `min(${Math.max(320, panelHeight)}px, calc(100% - ${MARGIN * 2}px))`

  /**
   * THE PRESENTATION PANEL NEEDS ONE NUMBER THE CSS CLAMP CANNOT GIVE IT: whether
   * there is room for a second window at all. "Render the panel or fall back to
   * inline cards" is a branch, and CSS `min()` cannot take a branch — so this one
   * measurement is done in JavaScript.
   *
   * MEASURED WITH A PROBE, NEVER `window.innerWidth`, for the reason the comment
   * above `panelWCss` spells out: a `position: fixed` element inside an ancestor
   * with `contain: paint` — the playground's preview card — is laid out against
   * THAT ANCESTOR, while `window.innerWidth` keeps reporting the window. The
   * probe below is a zero-height fixed element stretched `left: 0; right: 0`, so
   * its measured width IS the containing block's, whichever element that turns
   * out to be. Initialised generously so the first paint renders the configured
   * size rather than a cramped fallback that immediately un-cramps (the
   * reference's `useViewportWidth` makes the same opening bid).
   */
  const [containerW, setContainerW] = useState(1920)
  const probeRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const el = probeRef.current
    if (!el) return
    setContainerW(el.offsetWidth)
    // A widget on somebody else's page: a missing ResizeObserver must leave the
    // mount-time measurement standing, not throw.
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => setContainerW(el.offsetWidth))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  /**
   * THE JS MIRROR OF `panelWCss`. `chatW` below and the CSS expression above are
   * THE SAME FORMULA written in two languages — `max(PANEL_MIN_W, min(panelWidth,
   * container − margins − tab))` — and they must never disagree: this one decides
   * how much room the chat leaves for the presentation panel, that one decides
   * how wide the chat actually paints. Change either without the other and the
   * panel is positioned against a chat width the chat is not using.
   *
   * WHAT THESE CLAMPS SAY: the chat concedes nothing to the presentation panel
   * (it clamps against margins and tab exactly as before); the presentation
   * panel takes whatever is left, and if what is left is under `PRES_MIN_W` it
   * is not rendered at all — a 230 px card column is not a presentation, it is
   * a sliver — and the cards render inline in the transcript, exactly as this
   * variant drew them before the panel existed. `cramped` is that verdict.
   */
  const chatW = Math.max(PANEL_MIN_W, Math.min(panelWidth, containerW - 2 * MARGIN - width))
  const room = containerW - 2 * MARGIN - width - chatW - PRES_GAP
  const presW = Math.min(presentationWidth, room)
  const cramped = presW < PRES_MIN_W

  /**
   * SLIDE THE PANEL OUT WHENEVER THE AGENT SHOWS SOMETHING NEW — the reference's
   * own trigger (agent-widget.tsx): watch the LAST card's id, not the count,
   * because a replaced card keeps the count equal. Never auto-open when cramped —
   * there is no panel to open, and flipping the state anyway would pop one out
   * the moment a resize un-cramps, long after the card stopped being news. The
   * ref still advances in that branch so the same card cannot trigger twice.
   */
  const lastCardId = control.cards[control.cards.length - 1]?.id
  const prevLastCardId = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (lastCardId !== undefined && lastCardId !== prevLastCardId.current && !cramped) {
      setPresOpen(true)
    }
    prevLastCardId.current = lastCardId
  }, [lastCardId, cramped])

  /**
   * A NEW SESSION STARTS WITH THE PANEL CLOSED (feedback 2026-08-27, first live
   * run): `presOpen` used to survive from the previous call, so starting a
   * fresh session greeted the visitor with an open, EMPTY panel — the new
   * session begins with no cards, and an empty panel that nobody asked for
   * reads as something failing to load. `connecting` is the one phase every
   * new session passes through exactly once, so it is the reset point; the
   * auto-open ref resets with it so the new session's first card re-opens the
   * panel even in the (theoretical) case of a colliding call id. Mid-call
   * close/reopen of the WIDGET deliberately still restores the panel — same
   * session, same cards, see `presShown` below. (`phase` is the component's
   * existing binding of `control.phase`, declared above.)
   */
  useEffect(() => {
    if (phase === 'connecting') {
      setPresOpen(false)
      prevLastCardId.current = undefined
    }
  }, [phase])

  /** The panel is on screen only when the CHAT is: closing the chat collapses the
   *  presentation with it (the wrapper's width animates to 0 alongside the chat's
   *  slide-out), and `presOpen` survives so reopening MID-SESSION restores both —
   *  a NEW session resets it, see the `connecting` effect above. */
  const presShown = open && presOpen && !cramped

  /** Where the tab ends up when open: hard against the assembly's inner face —
   *  the presentation panel's when it is out, the chat's when it is not — and
   *  never past the far edge of whatever contains it. `presOpen`, not
   *  `presShown`: this value is only consumed while `open`, and folding `open`
   *  in would snap the tab home before its own close animation played. */
  const dockCss =
    presOpen && !cramped
      ? `min(calc(${MARGIN}px + ${panelWCss} + ${PRES_GAP + presW}px), calc(100% - ${width}px))`
      : `min(calc(${MARGIN}px + ${panelWCss}), calc(100% - ${width}px))`

  const left = side === 'left'
  /** Points away from the edge the tab is stuck to, so it flips with the side. */
  const sx = left ? 1 : -1
  const tabRadius = left ? `0 ${R_TAB}px ${R_TAB}px 0` : `${R_TAB}px 0 0 ${R_TAB}px`

  /**
   * THE SHADOW CHANGES WHEN THE TAB DOCKS, because a shadow is what says two things
   * are separate objects and the docked tab is not one.
   *
   * Closed it is the flag's two-part shadow, unchanged: an omnidirectional glow and
   * an offset layer, which is right for a tab floating against a page. Open, that
   * glow lands ON the panel the tab is butted against — the tab sits a z-index
   * above it — and paints a grey smear down the panel's edge, so the two read as a
   * tab overlapping a window rather than as one assembly.
   *
   * Open, therefore: one layer, pushed AWAY from the panel, with a negative spread
   * so it cannot reach back across the shared edge. The arithmetic is
   * `spread + blur/2 - offset` = `-5 + 7 - 10`, comfortably negative, which is the
   * whole trick — the shadow's own extent never reaches the touching face.
   */
  const tabShadow = open
    ? `${10 * sx}px 0 14px -5px rgba(0,0,0,0.22)`
    : `0 0 15px rgba(0,0,0,0.3), ${4 * -sx}px 0 10px rgba(0,0,0,0.1)`
  /** Two tones, so the focus ring is visible whether the page behind it is light
   *  or dark. Appended to the drop shadow because a box has only one of these. */
  const tabRing = ring ? `, 0 0 0 2px ${onAccent}, 0 0 0 5px ${accent}` : ''

  const tab: CSSProperties = {
    // No reset exists inside the shadow root, so everything a UA stylesheet would
    // have an opinion about is stated.
    boxSizing: 'border-box',
    appearance: 'none',
    border: 0,
    margin: 0,
    padding: 0,
    position: 'fixed',
    // THE INSET, NOT A `translateX`, and it is the price of the CSS-arithmetic
    // clamp above: a percentage inside `translate` is a percentage of the ELEMENT,
    // while a percentage in `left`/`right` is one of the containing block — and the
    // containing block is the whole point. `left` is not composited; this is one
    // 40 px box moving once per open, so the difference is not observable.
    ...(left ? { left: open ? dockCss : 0 } : { right: open ? dockCss : 0 }),
    // `offsetY` names the CENTRE of the tab. Once the panel is open the tab docks
    // to the PANEL's centre instead, so the two line up however the tab was
    // placed — the reference anchors its close tab to the widget for this reason.
    top: open ? '50%' : offsetY,
    transform: 'translateY(-50%)',
    zIndex: zIndex + 1,
    width,
    height: open ? CLOSE_TAB_H : height,
    borderRadius: tabRadius,
    background: accent,
    color: onAccent,
    boxShadow: tabShadow + tabRing,
    // Not the `font` shorthand: a shorthand followed by longhands depends on the
    // order React happens to assign them in. px, never rem — see the header.
    fontFamily: FONT_STACK,
    fontWeight: 700,
    // 18px, NOT a size chosen for this file: it is what `flag-button.tsx` sets, and
    // the two tabs sit on the same customer's site. A tab that is a different size
    // from the tab beside it reads as two products.
    fontSize: '18px',
    lineHeight: 1,
    // Normal, for the same reason. The first cut tracked the rotated label out by
    // 0.4px, which is a real difference at 18px over "Click to Talk".
    letterSpacing: 'normal',
    textTransform: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    cursor: 'pointer',
    outline: 'none',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    // A cheap stand-in for the `:hover` rule we cannot write.
    filter: hover ? 'brightness(0.94)' : 'none',
    transition: still
      ? 'none'
      : [
          `top ${SPRING_MS}ms ${SPRING}`,
          `left ${SPRING_MS}ms ${SPRING}`,
          `right ${SPRING_MS}ms ${SPRING}`,
          `height ${SPRING_MS}ms ${SPRING}`,
          `box-shadow ${FADE_MS}ms ease`,
          'filter 150ms ease',
        ].join(', '),
  }

  const panel: CSSProperties = {
    boxSizing: 'border-box',
    position: 'fixed',
    ...(left ? { left: MARGIN } : { right: MARGIN }),
    top: '50%',
    width: panelWCss,
    // `100%`, never `100vh` — see the note on `panelWCss`. Shared with the
    // presentation panel: see `panelHCss`.
    height: panelHCss,
    // Slid out by its own width PLUS the shadow's reach, so nothing of it shows
    // while closed. Written as two literals rather than as arithmetic on `sx`:
    // `calc(${sx * -100}% - 40px)` reads as symmetric and is not — on the right it
    // produced `calc(100% - 40px)`, which left 40 px of panel on screen through the
    // whole closing animation and only vanished when `visibility` flipped at the
    // end. A transform, not a `left`, because this is the one animation on screen
    // that moves a whole panel and it has to composite.
    transform: `translateY(-50%) translateX(${open ? '0px' : left ? 'calc(-100% - 40px)' : 'calc(100% + 40px)'})`,
    zIndex,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: R_PANEL,
    border: `1px solid ${surfaceBorder}`,
    background: surface,
    color: onSurface,
    boxShadow: PANEL_SHADOW,
    fontFamily: FONT_STACK,
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '14px',
    lineHeight: 1.5,
    letterSpacing: 'normal',
    textTransform: 'none',
    textAlign: 'left',
    // `visibility` IS IN THE TRANSITION LIST AND THAT IS THE TRICK: it is a
    // discrete property, so it flips at the START when becoming visible and at the
    // END when becoming hidden. The slide still plays, and once it has played the
    // closed panel stops being painted — which is what keeps a 380 px box off
    // every page this widget is installed on.
    visibility: open ? 'visible' : 'hidden',
    transition: still
      ? 'none'
      : `transform ${SPRING_MS}ms ${SPRING}, visibility ${SPRING_MS}ms ${SPRING}`,
  }

  return (
    <LightboxContext.Provider value={setLightbox}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-label={open ? 'Close the chat and end the call' : label}
        // `:focus-visible` needs a stylesheet; asking the element whether it
        // matches does not, and gives the same answer.
        onFocus={(e) => setRing(e.currentTarget.matches(':focus-visible'))}
        onBlur={() => setRing(false)}
        onPointerEnter={() => setHover(true)}
        onPointerLeave={() => setHover(false)}
        style={tab}
      >
        {/* Both layers stay mounted and cross-fade. Swapping them, as the reference
            does, means the label vanishes on the same frame the tab starts
            shrinking and the morph reads as two unrelated events. Both are
            `aria-hidden` because the button already has a name. */}
        <Layer visible={!open} still={still}>
          <span
            style={{
              // -90° reads bottom-to-top and is right for a left tab; a right tab
              // wants +90°, top-to-bottom. Get this wrong and it looks like a bug.
              transform: `rotate(${left ? -90 : 90}deg)`,
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </span>
        </Layer>
        <Layer visible={open} still={still}>
          <CloseGlyph size={18} />
        </Layer>
      </button>

      <div
        style={panel}
        // A chat window, not a modal: it does not trap focus and it does not dim
        // the page. `dialog` is still the right role — it is a named, dismissible
        // container — and `inert` is what keeps a closed one out of the tab order
        // during the 400 ms it is still technically visible.
        role="dialog"
        aria-label={title}
        inert={!open}
      >
        <Header
          control={control}
          title={title}
          caption={caption}
          accent={accent}
          onAccent={onAccent}
          still={still}
          onClose={toggle}
          // No toggle at all when cramped: a button that opens a panel which
          // cannot render is a control that does nothing, which is worse than
          // no control. Inline cards need no announcing dot either — they are
          // already in view.
          pres={
            cramped
              ? null
              : {
                  open: presOpen,
                  unread: control.cards.length > 0,
                  toggle: () => setPresOpen((v) => !v),
                }
          }
        />
        <Body
          control={control}
          accent={accent}
          onAccent={onAccent}
          surface={surface}
          onSurface={onSurface}
          surfaceBorder={surfaceBorder}
          still={still}
          open={open}
          inlineCards={cramped}
        />
        <Composer
          control={control}
          endLabel={endLabel}
          accent={accent}
          onAccent={onAccent}
          surface={surface}
          onSurface={onSurface}
          surfaceBorder={surfaceBorder}
          still={still}
        />
      </div>

      {/*
        THE PRESENTATION PANEL, as a WIDTH-COLLAPSE REVEAL — the reference's exact
        mechanism, translated to this file's fixed-position world. The outer
        wrapper is pinned to the chat window's inner face and clips; the inner
        element is pinned to the wrapper's CHAT-FACING edge at the panel's full
        width. So while the wrapper's width animates from 0, what emerges from the
        clip is the panel's outer edge first — the panel reads as sliding out from
        BEHIND the chat window, which is why it sits one z-index below it. Width,
        not transform, because a transform would carry the whole panel with the
        clip edge and the reveal would read as a wipe instead.

        The wrapper animates on the tab's own spring so the docked tab — whose
        `left`/`right` transition is what moves it past the panel — travels in
        lockstep with the panel's edge. The spring's overshoot only widens a
        transparent clip window past the inner element's fixed extent, so it
        reveals nothing.

        Not rendered at all when cramped: the fallback is the inline cards in the
        transcript, not a thinner panel — see the geometry block.
      */}
      {!cramped && (
        <div
          aria-hidden={!presShown}
          inert={!presShown}
          style={{
            boxSizing: 'border-box',
            position: 'fixed',
            // The chat panel's inner face: margin plus the chat's own CSS-clamped
            // width — the same `panelWCss` the chat paints with, so the two can
            // never disagree about where that face is.
            ...(left
              ? { left: `calc(${MARGIN}px + ${panelWCss})` }
              : { right: `calc(${MARGIN}px + ${panelWCss})` }),
            top: '50%',
            transform: 'translateY(-50%)',
            // PRES_BLEED of slack above, below and past the outer edge, so the
            // panel's shadow renders instead of being amputated at the clip —
            // see PRES_SHADOW. The chat-facing edge gets none: that edge's clip
            // is the slide-out reveal itself.
            height: `calc(${panelHCss} + ${2 * PRES_BLEED}px)`,
            width: presShown ? PRES_GAP + presW + PRES_BLEED : 0,
            overflow: 'hidden',
            zIndex: zIndex - 1,
            // The bleed is invisible but it is real page area, and a transparent
            // div swallowing clicks on somebody's site is a support ticket. The
            // panel itself opts back in.
            pointerEvents: 'none',
            transition: still ? 'none' : `width ${SPRING_MS}ms ${SPRING}`,
          }}
        >
          <div
            style={{
              boxSizing: 'border-box',
              position: 'absolute',
              top: PRES_BLEED,
              // Anchored to the chat-facing edge, with the gap as padding on that
              // same side — the daylight between the windows travels with the
              // reveal instead of arriving after it — and the shadow's bleed as
              // padding on the outer side.
              ...(left
                ? { left: 0, paddingLeft: PRES_GAP, paddingRight: PRES_BLEED }
                : { right: 0, paddingRight: PRES_GAP, paddingLeft: PRES_BLEED }),
              height: `calc(100% - ${2 * PRES_BLEED}px)`,
              width: PRES_GAP + presW + PRES_BLEED,
              pointerEvents: 'none',
            }}
          >
            <PresentationPanel
              cards={control.cards}
              left={left}
              accent={accent}
              onAccent={onAccent}
              surface={surface}
              onSurface={onSurface}
              surfaceBorder={surfaceBorder}
              still={still}
              onCollapse={() => setPresOpen(false)}
            />
          </div>
        </div>
      )}

      {/*
        THE PROBE that measures the containing block for the geometry above: a
        zero-height fixed element stretched edge to edge, so `offsetWidth` reports
        the width of whatever `position: fixed` is actually laid out against —
        the viewport on a customer's page, the preview card in the playground.
        Always rendered, costs nothing, paints nothing.
      */}
      <div
        ref={probeRef}
        aria-hidden
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 0,
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      />

      {/* Outside both, so the announcement is not entangled with either one's own
          name, and visually hidden without a stylesheet. */}
      <span aria-live="polite" style={SR_ONLY}>
        {withTakeover(control, statusSentence(phase, control.error, live))}
      </span>

      {/*
        CARDS STAY ANNOUNCED WHICHEVER SURFACE PAINTS THEM. Inline (cramped),
        they sit inside the transcript's own `role="log"` live region and announce
        the way every turn does. In the panel they cannot: a card's arrival is
        what OPENS the panel, so the card is inserted while the wrapper is still
        `aria-hidden` — a live region change inside a hidden subtree is never
        announced, and flipping `aria-hidden` afterwards does not replay it. So
        the panel path mirrors each card into this visually-hidden log instead.
        RAW text, not `renderMiniMarkdown`: rendered markdown would put focusable
        links inside an invisible region — a keyboard trap-in-waiting — and the
        occasional literal asterisk read aloud is the cheaper defect.
      */}
      {!cramped && (
        <div role="log" aria-live="polite" aria-label="Cards from the assistant" style={SR_ONLY}>
          {control.cards.map((c) => (
            <p key={c.id}>{c.title !== null ? `${c.title}. ${c.body}` : c.body}</p>
          ))}
        </div>
      )}
      {/* Above both windows, and mounted only while a picture is open. */}
      <Lightbox image={lightbox} onClose={closeLightbox} zIndex={zIndex + LIGHTBOX_Z_ABOVE} />
    </LightboxContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// The header
// ---------------------------------------------------------------------------

/**
 * Accent surface, `onAccent` ink, and every tint on it drawn as `currentColor` at
 * a low opacity — see the file header for why an alpha is not available here.
 */
function Header({
  control,
  title,
  caption,
  accent,
  onAccent,
  still,
  onClose,
  pres,
}: {
  control: VoiceControl
  title: string
  caption: string | undefined
  accent: string
  onAccent: string
  still: boolean
  onClose: () => void
  /** The presentation panel's header controls, or null when the geometry ruled
   *  the panel out (`cramped`) and there is nothing for a toggle to toggle. */
  pres: { open: boolean; unread: boolean; toggle: () => void } | null
}) {
  const live = isLive(control.phase)
  return (
    <div
      style={{
        boxSizing: 'border-box',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '12px 12px 12px 14px',
        background: accent,
        color: onAccent,
      }}
    >
      <span
        style={{
          position: 'relative',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 34,
          height: 34,
          borderRadius: '50%',
          overflow: 'hidden',
        }}
      >
        <Tint opacity={0.14} radius="50%" />
        <span style={{ position: 'relative', display: 'flex' }}>
          <WaveGlyph size={17} />
        </span>
      </span>

      <span style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span
          style={{
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </span>
        {caption ? (
          <span
            style={{
              fontSize: '10.5px',
              fontWeight: 400,
              lineHeight: 1.2,
              opacity: 0.7,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {caption}
          </span>
        ) : null}
      </span>

      <StatusPill phase={control.phase} still={still} />

      {/*
        MUTE IS THE ONLY AUDIO CONTROL HERE, and the reference's second one is
        missing because the capability is. That widget can also mute the AGENT —
        read the transcript instead of hearing it — which is a genuinely good idea
        for this variant in particular. `VoiceControl` carries `muted` and
        `toggleMute` for the microphone and nothing for the far end, and inventing
        a control that silences an `<audio>` element this component does not own
        would be a widget reaching past its interface. Written down rather than
        assumed: it belongs on `VoiceControl`, not here.
      */}
      {/*
        THE PRESENTATION TOGGLE, before the mute button — panel first, call
        controls after, the reference's own order. The dot is the unread signal:
        cards exist, panel closed. It is the ink at full strength rather than a
        third colour, positioned on a WRAPPER around the button because the
        button clips its own overflow (the hover wash needs it to), and a badge
        on a corner is exactly the thing overflow clipping eats.
      */}
      {pres !== null && (
        <span style={{ position: 'relative', flexShrink: 0, display: 'flex' }}>
          <IconButton
            label={pres.open ? 'Hide the presentation panel' : 'Show the presentation panel'}
            onClick={pres.toggle}
            pressed={pres.open}
            ink={onAccent}
            still={still}
          >
            <PanelGlyph size={16} />
          </IconButton>
          {!pres.open && pres.unread && (
            <span
              aria-hidden
              style={{
                position: 'absolute',
                top: 4,
                right: 4,
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: onAccent,
                pointerEvents: 'none',
              }}
            />
          )}
        </span>
      )}
      <IconButton
        label={control.muted ? 'Unmute the microphone' : 'Mute the microphone'}
        onClick={control.toggleMute}
        disabled={!live}
        pressed={control.muted}
        ink={onAccent}
        still={still}
      >
        {control.muted ? <MicOffGlyph size={16} /> : <MicGlyph size={16} />}
      </IconButton>
      <IconButton label="Close the chat and end the call" onClick={onClose} ink={onAccent} still={still}>
        <CloseGlyph size={15} />
      </IconButton>
    </div>
  )
}

/**
 * Connecting / Online / Speaking / Offline.
 *
 * THE DOT IS NEVER THE ONLY SIGNAL — it is `currentColor` at three opacities, not
 * three colours. A green dot on this yellow would be a third colour in a two-colour
 * object, and the word beside it already says which state this is, so a monochrome
 * screenshot and a colour-blind reader get the same information as anybody else.
 */
function StatusPill({ phase, still }: { phase: VoicePhase; still: boolean }) {
  const dotRef = useRef<HTMLSpanElement>(null)
  const connecting = phase === 'connecting'
  useLoop(dotRef, PULSE_FRAMES, PULSE_TIMING, connecting && !still)

  const word =
    phase === 'speaking' ? 'Speaking'
    : isLive(phase) ? 'Online'
    : connecting ? 'Connecting'
    : phase === 'error' ? 'Failed'
    : 'Offline'

  return (
    <span
      style={{
        position: 'relative',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        padding: '4px 8px',
        borderRadius: 999,
        overflow: 'hidden',
      }}
    >
      <Tint opacity={0.12} radius={999} />
      <span
        ref={dotRef}
        style={{
          position: 'relative',
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: 'currentColor',
          opacity: isLive(phase) ? 1 : connecting ? 0.8 : 0.4,
        }}
      />
      <span style={{ position: 'relative', fontSize: '10px', fontWeight: 600, whiteSpace: 'nowrap' }}>
        {word}
      </span>
    </span>
  )
}

// ---------------------------------------------------------------------------
// The transcript
// ---------------------------------------------------------------------------

/**
 * The scrolling body: the transcript, and the four things that stand in for it
 * when there is nothing to show yet.
 *
 * THE TINT LAYER IS A SIBLING OF THE SCROLLER, NOT ITS BACKGROUND. The body is a
 * shade off `surface` so that a bubble painted in plain `surface` reads as raised
 * — the reference's `bg-brand-surface/60` under `bg-card`. That shade cannot be an
 * alpha (see the file header), so it is a `currentColor` layer; and it has to sit
 * OUTSIDE the scrolling element or it would scroll away, leaving the tint on the
 * first screenful only.
 */
function Body({
  control,
  accent,
  onAccent,
  surface,
  onSurface,
  surfaceBorder,
  still,
  open,
  inlineCards,
}: {
  control: VoiceControl
  accent: string
  onAccent: string
  surface: string
  onSurface: string
  surfaceBorder: string
  still: boolean
  open: boolean
  /** True when the geometry ruled the presentation panel out (`cramped`) and
   *  cards fall back to rendering here, interleaved into the transcript. False
   *  means the panel owns them and this body renders none. */
  inlineCards: boolean
}) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const stick = useRef(true)
  const lines = control.transcript
  const phase = control.phase
  /**
   * `show_info` NEVER GETS A CHIP HERE. Its user-facing representation IS the
   * card — a "Show info · Completed" chip above the very card it produced is
   * the plumbing showing through, and an end user has no use for it (feedback
   * 2026-08-27, first live ServiceNow run). HTTP tools keep their chips: those
   * are the operator's chosen surface for "the agent is looking something up",
   * with display names written for exactly this audience. The authed /call
   * transcript still shows every call including this one — an operator wants
   * the plumbing.
   */
  const tools = control.tools.filter((t) => t.name !== 'show_info')
  const cards = control.cards

  /**
   * Stick to the bottom, but only if the reader is already there.
   *
   * Yanking somebody to the bottom while they are reading an earlier answer is the
   * defect this guard exists for, and a streaming reply fires this effect several
   * times a second, so it would be a violent one. `scrollTop` on OUR OWN element
   * rather than `scrollIntoView` on a child: inside a shadow root on somebody
   * else's page, `scrollIntoView` is entitled to scroll their document too.
   *
   * `useLayoutEffect`, so the jump happens in the same frame the new line paints
   * rather than one frame later — the visible difference is a flicker of the
   * previous scroll position under new text.
   */
  useLayoutEffect(() => {
    const el = scrollerRef.current
    if (!el || !stick.current) return
    // A PLAIN ASSIGNMENT, AND THE TRANSCRIPT DOES NOT SCROLL SMOOTHLY. Both of the
    // smooth routes were tried and both silently did nothing here — measured, not
    // guessed: after `scrollTo({ behavior: 'smooth' })` the scroller read back 0 a
    // full second later, and moving the smoothness onto the element as
    // `scroll-behavior: smooth` reproduced it exactly, because that makes the
    // assignment below animate by the same broken mechanism. Programmatic smooth
    // scrolling is unreliable inside a composited, `contain`-ed subtree — which is
    // where this element sits in the playground's preview card and can easily be on
    // a customer's page.
    //
    // So it snaps. Landing on the newest turn is the REQUIREMENT and the animation
    // was the nicety, and the nicety ate the requirement twice; a transcript that
    // jumps to the bottom is also what every messaging client does with incoming
    // messages. The guard above is what makes the snap tolerable: it only fires
    // when the reader is already at the bottom.
    //
    // `tools` IS IN THE DEPS AND WAS MISSING. A tool card is sixty-odd pixels of
    // new content in the middle of the flow, and without it the scroller stayed
    // wherever the last LINE had put it — leaving the newest turn clipped behind
    // the composer until somebody spoke again. `cards` is here for the same
    // reason, learned rather than re-learned: an info card is even taller.
    el.scrollTop = el.scrollHeight
  }, [lines, tools, cards, phase])

  // Re-anchor on open: a panel reopened onto an old transcript should show its end.
  useEffect(() => {
    const el = scrollerRef.current
    if (!open || !el) return
    stick.current = true
    el.scrollTop = el.scrollHeight
  }, [open])

  /**
   * Tools anchored after line `index - 1` — i.e. those whose call started when the
   * conversation stood `index` lines deep. `Math.min` clamps an anchor that ran
   * past the end, which happens when a tool fires before its own turn's line has
   * arrived: without the clamp that card would be filtered out by every index and
   * never render at all. Straight from `components/call/transcript.tsx`, so the two
   * places that draw this draw it the same way.
   */
  const toolsAt = (index: number) =>
    tools.filter((t) => Math.min(t.anchorIndex, lines.length) === index)

  /** Cards, by the same rule and the same clamp. At a shared index the tool
   *  chips render first: on a real call the `show_info` chip's `running` frame
   *  arrives before the card frame, so chip-then-card is arrival order.
   *
   *  THIS IS THE CRAMPED FALLBACK PATH, not dead code: with room for the
   *  presentation panel the cards render there and ONLY there (`inlineCards` is
   *  false and this returns nothing) — a card painted twice reads as two cards.
   *  The tool spinner chips above are NOT gated the same way: a chip is status,
   *  not content, and status stays in the conversation whichever surface the
   *  content went to. */
  const cardsAt = (index: number) =>
    inlineCards ? cards.filter((c) => Math.min(c.anchorIndex, lines.length) === index) : []

  // Cards only count against emptiness when they would render HERE: a card that
  // went to the presentation panel must not silently suppress the placeholder
  // of a transcript with nothing in it.
  const empty = lines.length === 0 && tools.length === 0 && (!inlineCards || cards.length === 0)

  return (
    <div style={{ position: 'relative', minHeight: 0, flex: 1, background: surface }}>
      <Tint opacity={0.045} />
      <div
        ref={scrollerRef}
        onScroll={(e) => {
          const el = e.currentTarget
          stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120
        }}
        /**
         * `log`, and `aria-live` on it, with the partial lines held back — see
         * `Line`. A live region that read every streaming token would narrate the
         * agent one syllable at a time over the top of the agent's own voice.
         */
        role="log"
        aria-live="polite"
        aria-label="Conversation transcript"
        style={{
          position: 'relative',
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          // `contain` keeps the scroll and the paint inside this box; without it a
          // host page with an unusual layout can be reflowed by our own scrolling.
          overscrollBehavior: 'contain',
          boxSizing: 'border-box',
          padding: '14px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {/* THE ERROR BOX IS THE PLACEHOLDER when there is one: both were rendering,
            so a failed call said "The call could not be connected" and then said
            which failure it was, one under the other. The box is the better half —
            it names the cause — so the generic line stands down. */}
        {empty && control.error === null ? (
          <Placeholder phase={phase} stage={control.stage ?? null} onSurface={onSurface} still={still} />
        ) : (
          <>
            {toolsAt(0).map((t) => (
              <ToolCard key={t.id} tool={t} surface={surface} surfaceBorder={surfaceBorder} still={still} />
            ))}
            {cardsAt(0).map((c) => (
              <InfoCard key={c.id} card={c} surface={surface} surfaceBorder={surfaceBorder} />
            ))}
            {lines.map((line, i) => (
              <Fragment key={line.id}>
                <Line
                  line={line}
                  accent={accent}
                  onAccent={onAccent}
                  surface={surface}
                  surfaceBorder={surfaceBorder}
                  still={still}
                />
                {toolsAt(i + 1).map((t) => (
                  <ToolCard key={t.id} tool={t} surface={surface} surfaceBorder={surfaceBorder} still={still} />
                ))}
                {cardsAt(i + 1).map((c) => (
                  <InfoCard key={c.id} card={c} surface={surface} surfaceBorder={surfaceBorder} />
                ))}
              </Fragment>
            ))}
          </>
        )}

        {control.error !== null && (
          <p
            style={{
              position: 'relative',
              // Centred when it is the only thing in the body, in flow when it is
              // the last line of a conversation that already happened.
              margin: empty ? 'auto 0' : 0,
              padding: '8px 10px',
              borderRadius: 10,
              border: `1px solid ${DANGER}`,
              color: DANGER,
              fontSize: '12px',
              lineHeight: 1.4,
              overflow: 'hidden',
            }}
          >
            <Tint opacity={0.05} radius={10} />
            <span style={{ position: 'relative' }}>{control.error}</span>
          </p>
        )}

        {/* Non-fatal things worth surfacing. The panel is the one variant with room
            to show them, so it does — the other four drop them on the floor. */}
        {control.notices.map((n) => (
          <p
            key={n}
            style={{
              margin: 0,
              textAlign: 'center',
              fontSize: '11px',
              lineHeight: 1.4,
              opacity: 0.6,
            }}
          >
            {n}
          </p>
        ))}
      </div>
    </div>
  )
}

/** One turn. */
function Line({
  line,
  accent,
  onAccent,
  surface,
  surfaceBorder,
  still,
}: {
  line: VoiceTranscriptLine
  accent: string
  onAccent: string
  surface: string
  surfaceBorder: string
  still: boolean
}) {
  const caretRef = useRef<HTMLSpanElement>(null)
  useLoop(caretRef, BLINK_FRAMES, BLINK_TIMING, !line.final && !still)

  const bubble: CSSProperties = {
    boxSizing: 'border-box',
    maxWidth: '86%',
    margin: 0,
    padding: '7px 11px',
    fontSize: '13.5px',
    lineHeight: 1.5,
    // A transcript is somebody's words: the newlines they typed are part of them,
    // and a long unbroken token (a URL, an order number read out) must wrap rather
    // than push the bubble past the panel.
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
  }

  if (line.who === 'user') {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <p
          // Held back from the live region until the provider has finished the
          // item, so a streaming line is announced once, whole.
          aria-hidden={!line.final}
          style={{
            ...bubble,
            borderRadius: '14px 14px 4px 14px',
            background: accent,
            color: onAccent,
          }}
        >
          {line.text || '…'}
        </p>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: AVATAR_GAP }}>
      <span
        aria-hidden
        style={{
          flexShrink: 0,
          marginTop: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: AVATAR,
          height: AVATAR,
          borderRadius: '50%',
          background: accent,
          color: onAccent,
        }}
      >
        <WaveGlyph size={13} />
      </span>
      <p
        aria-hidden={!line.final}
        style={{
          ...bubble,
          borderRadius: '14px 14px 14px 4px',
          background: surface,
          border: `1px solid ${surfaceBorder}`,
        }}
      >
        {line.text}
        {!line.final && (
          <span ref={caretRef} aria-hidden style={{ opacity: 0.55 }}>
            ▍
          </span>
        )}
      </p>
    </div>
  )
}

/**
 * A TOOL CALL, drawn the way `/call` draws one — same three lines, same words.
 *
 * The panel is a public embed's view of a conversation, and the control page is an
 * operator's view of the same one; a customer who watches a call on both should see
 * the same object. So this is `components/call/transcript.tsx`'s `ToolCard` in
 * inline styles: the icon and status line (Running… / Completed · 0.8s / Failed ·
 * 0.8s), the name as a title, and the raw name under it in mono.
 *
 * WHERE THE WORDS COME FROM. An embed visitor is not authed and cannot query the
 * tool library, so everything this card says arrived on the `{t:'tool'}` control
 * message itself: the raw name always, plus the operator-written display label
 * and blurb when the tool definition carries them. The label replaces the
 * auto-title (`titleOf`) and the blurb replaces the mono name in the second
 * slot; with neither set, the card is exactly what it was before display
 * metadata existed — auto-title over mono name, the same fallback branch the
 * control page uses when its own lookup comes back empty. Arguments and results
 * are absent from both: they never ride the control channel at all, since a
 * public visitor holds it.
 *
 * INDENTED BY THE AVATAR'S WIDTH so the card's left edge lines up with the agent's
 * bubbles rather than with the panel. A tool call IS the agent's side of the
 * conversation, and a card that starts 34 px to the left of every agent message
 * reads as a third speaker.
 */
function ToolCard({
  tool,
  surface,
  surfaceBorder,
  still,
}: {
  tool: VoiceToolCall
  surface: string
  surfaceBorder: string
  still: boolean
}) {
  const runningRef = useRef<HTMLSpanElement>(null)
  useLoop(runningRef, PULSE_FRAMES, PULSE_TIMING, tool.state === 'running' && !still)
  const took = tool.seconds === null ? '' : ` · ${tool.seconds.toFixed(1)}s`

  return (
    <div style={{ display: 'flex', paddingLeft: AVATAR + AVATAR_GAP }}>
      <div
        style={{
          boxSizing: 'border-box',
          maxWidth: '100%',
          padding: '8px 11px',
          borderRadius: 14,
          border: `1px solid ${surfaceBorder}`,
          background: surface,
        }}
      >
        <p
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            margin: 0,
            fontSize: '11px',
            lineHeight: 1.3,
            color: tool.state === 'error' ? DANGER : undefined,
            opacity: tool.state === 'error' ? 1 : 0.6,
          }}
        >
          <FunctionGlyph size={12} />
          {tool.state === 'running' ? (
            <span ref={runningRef}>Running…</span>
          ) : tool.state === 'ok' ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <TickGlyph size={10} />
              Completed{took}
            </span>
          ) : (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <CloseGlyph size={9} />
              Failed{took}
            </span>
          )}
        </p>
        <p style={{ margin: '3px 0 0', fontSize: '13px', fontWeight: 600, lineHeight: 1.35 }}>
          {tool.displayName ?? titleOf(tool.name)}
        </p>
        {tool.displayDescription !== null ? (
          <p
            style={{
              margin: '1px 0 0',
              fontSize: '11px',
              lineHeight: 1.35,
              opacity: 0.7,
              overflowWrap: 'break-word',
            }}
          >
            {tool.displayDescription}
          </p>
        ) : (
          <p
            style={{
              margin: '1px 0 0',
              fontSize: '11px',
              fontFamily: MONO_STACK,
              lineHeight: 1.35,
              opacity: 0.55,
              overflowWrap: 'break-word',
            }}
          >
            {tool.name}
          </p>
        )}
      </div>
    </div>
  )
}

/** `get_weather` -> `Get weather`: a title, not a rename — the mono name stays
 *  visible under it, because the mono name is what the session record uses. The
 *  control page's own `titleOf`, kept identical so the two never drift. */
function titleOf(name: string): string {
  const words = name.replace(/[_-]+/g, ' ').trim()
  return words === '' ? name : words[0]!.toUpperCase() + words.slice(1)
}

/**
 * The card body's typography, handed to the whitelist renderer as inline styles
 * for the file's usual reason: no stylesheet exists here to carry it. A module
 * constant, not a literal at the call site, so a re-render hands the renderer
 * the same object. Spacing between blocks and the list indent are the
 * renderer's own defaults; margins are deliberately NOT set here so they stay
 * its job. Links keep the surface ink and an underline rather than taking
 * `accent`: accent is a SURFACE colour (the house yellow), and yellow text on
 * a white card is not a link, it is a smudge.
 */
const CARD_BODY: MiniMarkdownStyles = {
  paragraph: { fontSize: '12.5px', lineHeight: 1.5, overflowWrap: 'break-word' },
  list: { fontSize: '12.5px', lineHeight: 1.5 },
  listItem: { overflowWrap: 'break-word' },
}

/**
 * A SCREEN CARD from the built-in `show_info` tool, INLINE in the transcript —
 * the cramped-geometry fallback, and the only rendering a card had before the
 * presentation panel landed.
 *
 * Styled as ToolCard's sibling on purpose: same indent (the avatar's width, so
 * its left edge lines up with the agent's bubbles — a card IS the agent's side
 * of the conversation), same border, same surface, same 14 px radius. The
 * bubble itself is `InfoCardBubble`, shared with the presentation panel, which
 * draws the same object full-width — one bubble, two homes, so the two surfaces
 * cannot drift apart card by card.
 */
function InfoCard({
  card,
  surface,
  surfaceBorder,
}: {
  card: VoiceCard
  surface: string
  surfaceBorder: string
}) {
  return (
    <div style={{ display: 'flex', paddingLeft: AVATAR + AVATAR_GAP }}>
      <InfoCardBubble card={card} surface={surface} surfaceBorder={surfaceBorder} />
    </div>
  )
}

/**
 * The card bubble itself — the model putting something in writing while it says
 * a short summary out loud. Width comes from the PARENT on purpose: in the
 * transcript it sits in a flex ROW and shrinks to its content like a bubble; in
 * the presentation panel it is a stretched flex-COLUMN child and fills the
 * panel, no avatar indent, like a page.
 *
 * THE BODY GOES THROUGH `renderMiniMarkdown` AND NOTHING ELSE. That renderer is
 * the whole security stance — React elements only, https-only links, angle
 * brackets are text — and this component adding any other path from `card.body`
 * to the DOM would be the hole. See lib/mini-markdown.tsx's header.
 */
function InfoCardBubble({
  card,
  surface,
  surfaceBorder,
}: {
  card: VoiceCard
  surface: string
  surfaceBorder: string
}) {
  // The widget's one lightbox, reached through context because a card lives in
  // two homes (the transcript and the presentation panel) and the modal must
  // sit above both — see `Lightbox`. Null outside a ChatFlag (a test rendering
  // the bubble alone), where images stay plain pictures.
  const enlarge = useContext(LightboxContext)
  return (
    <div
      style={{
        boxSizing: 'border-box',
        maxWidth: '100%',
        padding: '9px 12px',
        borderRadius: 14,
        border: `1px solid ${surfaceBorder}`,
        background: surface,
      }}
    >
      {card.title !== null && (
        <p style={{ margin: '0 0 5px', fontSize: '13px', fontWeight: 600, lineHeight: 1.35 }}>
          {card.title}
        </p>
      )}
      {renderMiniMarkdown(card.body, CARD_BODY, enlarge === null ? {} : { onImageClick: enlarge })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The lightbox
// ---------------------------------------------------------------------------

/**
 * `open(image)` for whichever card wants a picture shown large. Provided once,
 * at the ChatFlag root, so the transcript's inline cards and the presentation
 * panel's cards share ONE modal that sits above both windows.
 */
const LightboxContext = createContext<((image: MiniMarkdownImage) => void) | null>(null)

/** Clear of both windows: the panel is at `zIndex`, its tab at `zIndex + 1`. */
const LIGHTBOX_Z_ABOVE = 2

/**
 * THE LIGHTBOX (2026-09-01). A card is a glance at ~340 px, and a screenshot at
 * that width is legible as a shape, not as words — the Run dialog's
 * `outlook.exe /safe` cannot be read off it. Clicking the picture opens it at
 * up to the viewport's size, on a dimmed backdrop, with its caption beneath.
 *
 * WHAT IT IS AND IS NOT. It shows the SAME image the card already loaded —
 * same https-only url the whitelist renderer accepted, same `no-referrer` —
 * larger. No new fetch policy, no new url source, nothing the card could not
 * already display; the security stance is the renderer's, unchanged, and this
 * component adds a size. It is a real dialog: `role="dialog"`, `aria-modal`,
 * named by the caption; the close control takes focus on open; Escape, the
 * backdrop and the × all close it; and focus returns to the picture that
 * opened it, which the renderer hands over as `trigger`.
 *
 * ESCAPE IS CAUGHT AT THE DOCUMENT, IN THE CAPTURE PHASE, and stopped there.
 * Inside a closed shadow root the widget's own key handlers are React handlers
 * on the widget tree; an Escape that reached them would read as "close the
 * chat" while the caller only meant "close the picture".
 *
 * Inline styles and px throughout, like everything else in this file — the
 * embed has no stylesheet to lean on. No animation: the picture is the point,
 * and it appears at once.
 */
function Lightbox({
  image,
  onClose,
  zIndex,
}: {
  image: MiniMarkdownImage | null
  onClose: () => void
  zIndex: number
}) {
  const closeRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (image === null) return
    closeRef.current?.focus()
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      e.preventDefault()
      onClose()
    }
    document.addEventListener('keydown', onKey, true)
    const trigger = image.trigger
    return () => {
      document.removeEventListener('keydown', onKey, true)
      // Back to the picture that opened it, if it is still on the page.
      if (trigger && trigger.isConnected) trigger.focus()
    }
  }, [image, onClose])

  if (image === null) return null
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={image.alt !== '' ? image.alt : 'Enlarged image'}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 28,
        boxSizing: 'border-box',
        background: 'rgba(0, 0, 0, 0.74)',
        fontFamily: FONT_STACK,
        cursor: 'zoom-out',
      }}
    >
      <figure
        // A click on the picture itself is not a dismissal — a reader leaning in
        // to look must not have it vanish under the pointer.
        onClick={(e) => e.stopPropagation()}
        style={{
          margin: 0,
          maxWidth: 'min(1100px, 100%)',
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
          cursor: 'default',
        }}
      >
        <img
          src={image.src}
          alt={image.alt}
          decoding="async"
          referrerPolicy="no-referrer"
          style={{
            display: 'block',
            maxWidth: '100%',
            // Room for the caption and the padding above and below.
            maxHeight: 'calc(100vh - 120px)',
            width: 'auto',
            height: 'auto',
            borderRadius: 10,
            background: '#ffffff',
            boxShadow: '0 24px 64px rgba(0, 0, 0, 0.55)',
          }}
        />
        {image.alt !== '' ? (
          <figcaption
            style={{
              color: '#ffffff',
              fontSize: '13px',
              lineHeight: 1.45,
              textAlign: 'center',
              maxWidth: '70ch',
              opacity: 0.92,
            }}
          >
            {image.alt}
          </figcaption>
        ) : null}
      </figure>
      <button
        ref={closeRef}
        type="button"
        onClick={onClose}
        aria-label="Close"
        title="Close (Esc)"
        style={{
          position: 'absolute',
          top: 14,
          right: 14,
          width: 36,
          height: 36,
          boxSizing: 'border-box',
          appearance: 'none',
          border: 0,
          margin: 0,
          padding: 0,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.16)',
          color: '#ffffff',
          fontFamily: FONT_STACK,
          fontSize: '22px',
          lineHeight: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        ×
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The presentation panel
// ---------------------------------------------------------------------------

/**
 * THE SECOND WINDOW: the reference's `PresentationPanel`, carrying the one card
 * kind this platform has (see item 1 of the file header). Same surface recipe
 * as the chat window — border, background, ink, radius — but its OWN, tighter
 * shadow (`PRES_SHADOW`, and its comment for why sharing `PANEL_SHADOW` was a
 * measured mistake) — with the chat header's accent/onAccent strip on top,
 * thinner, so the chat stays the senior window of the pair.
 *
 * The geometry that positions and reveals it lives in `ChatFlag`; this
 * component is only what is INSIDE the reveal.
 */
function PresentationPanel({
  cards,
  left,
  accent,
  onAccent,
  surface,
  onSurface,
  surfaceBorder,
  still,
  onCollapse,
}: {
  cards: readonly VoiceCard[]
  left: boolean
  accent: string
  onAccent: string
  surface: string
  onSurface: string
  surfaceBorder: string
  still: boolean
  onCollapse: () => void
}) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const lastCardRef = useRef<HTMLDivElement>(null)
  const lastCardId = cards[cards.length - 1]?.id
  const prevLastCardId = useRef<string | undefined>(undefined)

  /**
   * REVEAL THE NEWEST CARD'S TOP when a new card arrives — not its bottom, which
   * is what stick-to-bottom would show of a card taller than the panel. The
   * reference does the same, plus a ResizeObserver that re-aligns while markdown
   * images load; NOT ported, because our markdown subset has no images (see
   * lib/mini-markdown.tsx — paragraphs, emphasis, lists, links) and nothing else
   * here reflows late.
   *
   * A PLAIN ASSIGNMENT, NOT `scrollTo({ behavior: 'smooth' })`, for the reason
   * the transcript's own scroll comment records at length: programmatic smooth
   * scrolling was measured to silently do NOTHING inside the composited,
   * `contain`-ed subtree the playground preview puts this widget in. Landing on
   * the card is the requirement, the animation was the nicety, and that nicety
   * has eaten this requirement in this file before. `useLayoutEffect` so the
   * jump lands in the same frame the card paints.
   */
  useLayoutEffect(() => {
    if (lastCardId !== undefined && lastCardId !== prevLastCardId.current) {
      const scroller = scrollerRef.current
      const el = lastCardRef.current
      if (scroller && el) scroller.scrollTop = Math.max(0, el.offsetTop - 8)
    }
    prevLastCardId.current = lastCardId
  }, [lastCardId])

  return (
    <div
      style={{
        boxSizing: 'border-box',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: R_PANEL,
        border: `1px solid ${surfaceBorder}`,
        background: surface,
        color: onSurface,
        boxShadow: PRES_SHADOW,
        // Opting back in: the two wrappers above pass pointer events through so
        // their invisible shadow-bleed cannot swallow clicks on the host page.
        pointerEvents: 'auto',
        // Its own typography, restated: this panel is a separate fixed-position
        // subtree, not a child of the chat window, so it inherits nothing from it.
        fontFamily: FONT_STACK,
        fontWeight: 400,
        fontStyle: 'normal',
        fontSize: '14px',
        lineHeight: 1.5,
        letterSpacing: 'normal',
        textTransform: 'none',
        textAlign: 'left',
      }}
    >
      {/* The header strip: the chat header's livery, thinner. */}
      <div
        style={{
          boxSizing: 'border-box',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 8px 10px 14px',
          background: accent,
          color: onAccent,
        }}
      >
        <span
          style={{
            minWidth: 0,
            flex: 1,
            fontSize: '13px',
            fontWeight: 600,
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          Presentation
        </span>
        <IconButton label="Hide the presentation panel" onClick={onCollapse} ink={onAccent} still={still}>
          {/* Pointing at the page edge the assembly docks to, because that is
              where the panel goes when it collapses — flips with `side`. */}
          <ChevronsGlyph size={15} left={left} />
        </IconButton>
      </div>

      {/* The card stack. Same tint-behind-the-scroller construction as the chat
          body, same reason: a shade off `surface` so the cards read as raised,
          and the shade must not scroll away with the first screenful. */}
      <div style={{ position: 'relative', minHeight: 0, flex: 1, background: surface }}>
        <Tint opacity={0.045} />
        <div
          ref={scrollerRef}
          style={{
            position: 'relative',
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehavior: 'contain',
            boxSizing: 'border-box',
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          {cards.length === 0 ? (
            <div
              style={{
                display: 'flex',
                flex: 1,
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 24px',
                textAlign: 'center',
              }}
            >
              <p style={{ margin: 0, fontSize: '12.5px', lineHeight: 1.5, opacity: 0.6 }}>
                When the agent has something to show, it appears here.
              </p>
            </div>
          ) : (
            cards.map((card, i) => (
              // The wrapper div is what `offsetTop` is read from: a direct child
              // of the positioned scroller, so the number means "from the top of
              // the scrollable content" with no offsetParent walking.
              <div key={card.id} ref={i === cards.length - 1 ? lastCardRef : undefined}>
                <InfoCardBubble card={card} surface={surface} surfaceBorder={surfaceBorder} />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * What the body says with nothing in it yet, which is three different things.
 *
 * NO `error` BRANCH: a failed call has a box of its own carrying the actual reason,
 * and this said the generic version of the same sentence directly above it.
 *
 * `idle` is in the list and is not dead code: the panel keeps its transcript after
 * a call ends, so a visitor who closes it and opens it again passes through this
 * branch on the way to a new session.
 */
/** The stage narration is `STAGE_TEXT` in stage-text.ts, shared with the side
 *  flag's pill so the two flags say the same four things in the same order. */
function Placeholder({
  phase,
  stage,
  onSurface,
  still,
}: {
  phase: VoicePhase
  stage: VoiceStartStage | null
  onSurface: string
  still: boolean
}) {
  const text =
    phase === 'connecting' ? connectingText(stage, 'Connecting you now')
    : isLive(phase) ? 'Say hello — or type a message below'
    : 'The conversation will appear here'

  return (
    <div
      style={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: '0 24px',
        textAlign: 'center',
      }}
    >
      {phase === 'connecting' ? <Dots ink={onSurface} still={still} /> : null}
      <p style={{ margin: 0, fontSize: '12.5px', lineHeight: 1.5, opacity: 0.6 }}>{text}</p>
    </div>
  )
}

/** Three staggered dots, animated through the Web Animations API because
 *  `@keyframes` needs a stylesheet and this file has none. */
function Dots({ ink, still }: { ink: string; still: boolean }) {
  const a = useRef<HTMLSpanElement>(null)
  const b = useRef<HTMLSpanElement>(null)
  const c = useRef<HTMLSpanElement>(null)
  useLoop(a, PULSE_FRAMES, DOT_TIMING_A, !still)
  useLoop(b, PULSE_FRAMES, DOT_TIMING_B, !still)
  useLoop(c, PULSE_FRAMES, DOT_TIMING_C, !still)
  const dot: CSSProperties = { width: 7, height: 7, borderRadius: '50%', background: ink, opacity: 0.35 }
  return (
    <span aria-hidden style={{ display: 'flex', gap: 5 }}>
      <span ref={a} style={dot} />
      <span ref={b} style={dot} />
      <span ref={c} style={dot} />
    </span>
  )
}

// ---------------------------------------------------------------------------
// The composer
// ---------------------------------------------------------------------------

/**
 * The text box, the send button, and the one line under them that says what the
 * call is doing.
 *
 * THE TEXT BOX IS NOT A FALLBACK FOR A BROKEN MICROPHONE HERE, which is what it is
 * on the conversation bar. This variant's whole proposition is that you can read
 * the conversation and join it in writing, so the composer is present from the
 * first frame rather than behind a toggle, and `allowText` is not a setting this
 * widget offers: a chat panel with the chat turned off is a different product.
 */
function Composer({
  control,
  endLabel,
  accent,
  onAccent,
  surface,
  onSurface,
  surfaceBorder,
  still,
}: {
  control: VoiceControl
  endLabel: string
  accent: string
  onAccent: string
  surface: string
  onSurface: string
  surfaceBorder: string
  still: boolean
}) {
  const [text, setText] = useState('')
  const live = isLive(control.phase)
  const phase = control.phase
  const canSend = live && text.trim() !== ''

  const send = useCallback(() => {
    const body = text.trim()
    if (!body || !isLive(control.phase)) return
    control.sendText(body)
    setText('')
  }, [control, text])

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // `isComposing` is the IME guard: pressing Enter to accept a candidate in
      // Japanese or Chinese must not send the half-finished line.
      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault()
        send()
      }
    },
    [send],
  )

  return (
    <div
      style={{
        boxSizing: 'border-box',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 7,
        padding: '10px 12px 11px',
        borderTop: `1px solid ${surfaceBorder}`,
        background: surface,
      }}
    >
      <div
        style={{
          boxSizing: 'border-box',
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-end',
          gap: 8,
          padding: 6,
          paddingLeft: 12,
          borderRadius: 20,
          border: `1px solid ${surfaceBorder}`,
          overflow: 'hidden',
        }}
      >
        <Tint opacity={0.04} radius={20} />
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          // NO `placeholder` ATTRIBUTE, and it is a real defect rather than a
          // preference: `::placeholder` is a pseudo-element, a pseudo-element needs
          // a stylesheet, and this file has none — so the UA's grey is the only
          // colour a native placeholder could be, which is unreadable against the
          // dark surface an operator is invited to set. The span below is the same
          // text in their own ink, and `pointerEvents: 'none'` means clicking it
          // still lands in the textarea.
          aria-label="Type a message"
          disabled={!live}
          style={{
            position: 'relative',
            boxSizing: 'border-box',
            flex: 1,
            minWidth: 0,
            // `fieldSizing` grows the box with the text where it is supported and
            // is ignored where it is not, which leaves the one-row default — so
            // this is an enhancement rather than a dependency.
            fieldSizing: 'content',
            maxHeight: 108,
            margin: 0,
            padding: '7px 0',
            border: 0,
            background: 'transparent',
            color: onSurface,
            fontFamily: FONT_STACK,
            fontSize: '13.5px',
            lineHeight: 1.45,
            resize: 'none',
            overflowY: 'auto',
            // No border and no focus ring: the pill around it is the frame, and a
            // ring that is on for the whole life of an always-present composer is
            // not telling anybody anything.
            outline: 'none',
            opacity: live ? 1 : 0.5,
          } as CSSProperties}
        />
        {text === '' && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: 12,
              top: 13,
              fontSize: '13.5px',
              lineHeight: 1.45,
              opacity: 0.42,
              pointerEvents: 'none',
            }}
          >
            {live ? 'Type a message, or just talk…'
              : phase === 'connecting' ? 'Connecting…'
              : 'Start the call to type'}
          </span>
        )}
        <button
          type="button"
          onClick={send}
          disabled={!canSend}
          aria-label="Send message"
          style={{
            position: 'relative',
            boxSizing: 'border-box',
            flexShrink: 0,
            appearance: 'none',
            border: 0,
            margin: 0,
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 30,
            height: 30,
            borderRadius: '50%',
            background: accent,
            color: onAccent,
            opacity: canSend ? 1 : 0.4,
            cursor: canSend ? 'pointer' : 'default',
            outline: 'none',
            WebkitTapHighlightColor: 'transparent',
            transition: still ? 'none' : 'opacity 150ms ease',
          }}
        >
          <ArrowUpGlyph size={15} />
        </button>
      </div>

      <StatusRow
        control={control}
        endLabel={endLabel}
        accent={accent}
        onAccent={onAccent}
        onSurface={onSurface}
        surfaceBorder={surfaceBorder}
        still={still}
        phase={phase}
      />
    </div>
  )
}

/**
 * The line under the composer: a meter and a sentence on the left, the one control
 * that changes the call on the right.
 *
 * The meter is `FlagBars` — the same ported pair of ten-bar visualisers the flag
 * shows in its expanded pill, in the panel's ink rather than the flag's. Reusing it
 * is the point: a customer running both flags on one site should see the same
 * object metering the same call.
 */
function StatusRow({
  control,
  endLabel,
  accent,
  onAccent,
  onSurface,
  surfaceBorder,
  still,
  phase,
}: {
  control: VoiceControl
  endLabel: string
  accent: string
  onAccent: string
  onSurface: string
  surfaceBorder: string
  still: boolean
  phase: VoicePhase
}) {
  const live = isLive(control.phase)
  const sentence =
    control.muted ? 'Muted — tap the mic to talk'
    : live && control.takenOver ? TAKEN_OVER_TEXT
    : phase === 'speaking' ? 'Speaking — talk to interrupt'
    : phase === 'thinking' ? 'Thinking…'
    : live ? 'Listening'
    : phase === 'connecting' ? 'Connecting…'
    // NOT "ended unexpectedly": most errors here are a microphone that was never
    // opened, so nothing ended. The box above says which failure it was.
    : phase === 'error' ? 'Not connected'
    : 'The call has ended'

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 2px' }}>
      {live && !control.muted ? (
        <FlagBars control={control} active color={onSurface} meterWidth={26} meterHeight={12} gap={3} />
      ) : null}
      <span
        style={{
          minWidth: 0,
          flex: 1,
          fontSize: '10.5px',
          fontWeight: 500,
          lineHeight: 1.3,
          opacity: 0.6,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {sentence}
      </span>

      {/*
        ONE CONTROL, TWO WORDS, AND IT IS NOT A CLOSE BUTTON. Hanging up and
        dismissing the panel are different intentions here: the whole reason this
        variant keeps its transcript after `ended` is so somebody can read it, and
        a visitor who wants the agent to stop talking should not have to throw the
        conversation away to get that. So the header's ✕ closes (and stops), and
        this ends the call in place.
      */}
      {live || phase === 'connecting' ? (
        <TextButton
          label={endLabel}
          onClick={control.stop}
          // Guarded rather than disabled while connecting, the flag's trade: a
          // dimmed hang-up is worse than an inert one, and the cost — a connect
          // that hangs cannot be cancelled from here — is the recoverable half.
          disabled={phase === 'connecting'}
          ink={onSurface}
          border={surfaceBorder}
          still={still}
        />
      ) : (
        <TextButton
          label={phase === 'error' ? 'Try again' : 'Start again'}
          onClick={control.start}
          filled={{ background: accent, color: onAccent }}
          ink={onSurface}
          border={surfaceBorder}
          still={still}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------

/** One cross-fading face of the tab. Both stay mounted — see the note at the call
 *  site for why `visibility` is in the transition list. */
function Layer({ visible, still, children }: { visible: boolean; still: boolean; children: ReactNode }) {
  return (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: visible ? 1 : 0,
        visibility: visible ? 'visible' : 'hidden',
        transition: still ? 'none' : `opacity ${FADE_MS}ms ease, visibility ${FADE_MS}ms ease`,
      }}
    >
      {children}
    </span>
  )
}

/**
 * A wash of `currentColor` over whatever is behind it.
 *
 * This is how every tint in the file is drawn, and the reason is in the header:
 * `accent` and `surface` are arbitrary CSS colours, so "12% of the header colour"
 * is not a value this component can compute. A layer of the INK at a low opacity
 * is, it works against any colour an operator can type, and it needs no parsing.
 * The caller positions it inside a `position: relative` box and puts its own
 * content in a `position: relative` sibling so the wash stays underneath.
 */
function Tint({ opacity, radius }: { opacity: number; radius?: number | string }) {
  return (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        background: 'currentColor',
        opacity,
        borderRadius: radius,
        pointerEvents: 'none',
      }}
    />
  )
}

/** A 30 px square icon control for the header, with the hover and focus states we
 *  cannot write as CSS rules. */
function IconButton({
  label,
  onClick,
  disabled = false,
  pressed = false,
  ink,
  still,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  pressed?: boolean
  ink: string
  still: boolean
  children: ReactNode
}) {
  const [hover, setHover] = useState(false)
  const [ring, setRing] = useState(false)
  // Pressed reads stronger than hovered, which reads stronger than resting — one
  // scale, so "is this on?" is answerable without a colour.
  const wash = pressed ? 0.24 : hover && !disabled ? 0.14 : 0
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      aria-pressed={pressed}
      onFocus={(e) => setRing(e.currentTarget.matches(':focus-visible'))}
      onBlur={() => setRing(false)}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      style={{
        position: 'relative',
        boxSizing: 'border-box',
        flexShrink: 0,
        appearance: 'none',
        border: 0,
        margin: 0,
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 30,
        height: 30,
        borderRadius: 8,
        background: 'transparent',
        color: ink,
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'default' : 'pointer',
        outline: 'none',
        overflow: 'hidden',
        WebkitTapHighlightColor: 'transparent',
        boxShadow: ring ? `0 0 0 2px ${ink}` : 'none',
        transition: still ? 'none' : 'opacity 150ms ease, box-shadow 150ms ease',
      }}
    >
      {wash > 0 && <Tint opacity={wash} radius={8} />}
      <span style={{ position: 'relative', display: 'flex' }}>{children}</span>
    </button>
  )
}

/** The footer's end/restart control: a small pill, outlined or filled. */
function TextButton({
  label,
  onClick,
  disabled = false,
  filled,
  ink,
  border,
  still,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  filled?: { background: string; color: string }
  ink: string
  border: string
  still: boolean
}) {
  const [hover, setHover] = useState(false)
  const [ring, setRing] = useState(false)
  return (
    <button
      type="button"
      onClick={() => {
        if (!disabled) onClick()
      }}
      aria-disabled={disabled || undefined}
      onFocus={(e) => setRing(e.currentTarget.matches(':focus-visible'))}
      onBlur={() => setRing(false)}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      style={{
        boxSizing: 'border-box',
        flexShrink: 0,
        appearance: 'none',
        margin: 0,
        padding: '4px 10px',
        borderRadius: 999,
        border: filled ? 0 : `1px solid ${border}`,
        background: filled ? filled.background : 'transparent',
        color: filled ? filled.color : ink,
        fontFamily: FONT_STACK,
        fontSize: '10.5px',
        fontWeight: 600,
        lineHeight: 1.4,
        letterSpacing: 'normal',
        textTransform: 'none',
        whiteSpace: 'nowrap',
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? 'default' : 'pointer',
        outline: 'none',
        WebkitTapHighlightColor: 'transparent',
        filter: hover && !disabled ? 'brightness(0.94)' : 'none',
        boxShadow: ring ? `0 0 0 2px ${ink}` : 'none',
        transition: still ? 'none' : 'opacity 150ms ease, filter 150ms ease, box-shadow 150ms ease',
      }}
    >
      {label}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Glyphs. All `currentColor`, so each one is black on the yellow and the panel's
// ink on the panel without being told which.
// ---------------------------------------------------------------------------

function WaveGlyph({ size }: { size: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden focusable="false">
      <rect x="2" y="10" width="2.2" height="4" rx="1.1" />
      <rect x="6.4" y="7" width="2.2" height="10" rx="1.1" />
      <rect x="10.9" y="3.5" width="2.2" height="17" rx="1.1" />
      <rect x="15.4" y="7" width="2.2" height="10" rx="1.1" />
      <rect x="19.8" y="10" width="2.2" height="4" rx="1.1" />
    </svg>
  )
}

/* `MicGlyph` and `MicOffGlyph` moved to mic-glyphs.tsx (2026-09-01), shared with
   the side flag's mute control so the two flags show the same microphone. */

/** `{ }` — a function ran. The control page reaches for lucide's square-function
 *  here; this file draws its own for the reason every glyph in it is hand-drawn. */
function FunctionGlyph({ size }: { size: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden focusable="false" style={{ flexShrink: 0 }}>
      <path
        d="M9 3.5H8A2.5 2.5 0 0 0 5.5 6v3.5A2.5 2.5 0 0 1 3 12a2.5 2.5 0 0 1 2.5 2.5V18A2.5 2.5 0 0 0 8 20.5h1M15 3.5h1A2.5 2.5 0 0 1 18.5 6v3.5A2.5 2.5 0 0 0 21 12a2.5 2.5 0 0 0-2.5 2.5V18a2.5 2.5 0 0 1-2.5 2.5h-1"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  )
}

function TickGlyph({ size }: { size: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden focusable="false" style={{ flexShrink: 0 }}>
      <path
        d="M4 12.5 9.5 18 20 6"
        stroke="currentColor"
        strokeWidth={2.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  )
}

function CloseGlyph({ size }: { size: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden focusable="false">
      <path
        d="M5 5l14 14M19 5L5 19"
        stroke="currentColor"
        strokeWidth={2.4}
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  )
}

/** A window with a second pane split off — the presentation panel toggle. */
function PanelGlyph({ size }: { size: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden focusable="false">
      <rect
        x="3"
        y="4.5"
        width="18"
        height="15"
        rx="2.5"
        stroke="currentColor"
        strokeWidth={2}
        fill="none"
      />
      <path d="M14.5 4.5v15" stroke="currentColor" strokeWidth={2} />
    </svg>
  )
}

/** `»`, or `«` when `left` — the presentation panel's collapse control. Drawn
 *  pointing right and mirrored with a transform rather than drawn twice, so the
 *  two directions cannot be edited apart. */
function ChevronsGlyph({ size, left }: { size: number; left: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden
      focusable="false"
      style={left ? { transform: 'scaleX(-1)' } : undefined}
    >
      <path
        d="M6 6l6 6-6 6M13 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth={2.4}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  )
}

function ArrowUpGlyph({ size }: { size: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden focusable="false">
      <path
        d="M12 20V4M5 11l7-7 7 7"
        stroke="currentColor"
        strokeWidth={2.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Wording, and the two hooks that replace CSS this file cannot have
// ---------------------------------------------------------------------------

/**
 * What the live region says, and what it refuses to say.
 *
 * The flag's rule, for the flag's reason: `listening`, `thinking` and `speaking`
 * alternate several times a minute, and piping them into a polite live region
 * interrupts a screen-reader user mid-turn with "thinking… listening… speaking…"
 * while they are trying to hear the agent. Only the coarse transitions are
 * announced. The transcript's own `role="log"` is where the CONTENT arrives, one
 * finished line at a time — see `Line`.
 */
function statusSentence(phase: VoicePhase, error: string | null, live: boolean): string {
  if (live) return 'Connected. The call is running.'
  switch (phase) {
    case 'idle':
      return ''
    case 'connecting':
      return 'Connecting.'
    case 'ended':
      return 'Call ended.'
    case 'error':
      return error ?? 'The call could not be connected.'
    default:
      return ''
  }
}

const SR_ONLY: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  border: 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  clipPath: 'inset(50%)',
}

/**
 * Module constants, not inline literals, and that is load-bearing: `useLoop`
 * depends on the identity of what it is handed, so a fresh array every render
 * would cancel and restart the animation sixty times a second.
 */
const BLINK_FRAMES: Keyframe[] = [{ opacity: 0.15 }, { opacity: 0.85 }, { opacity: 0.15 }]
const BLINK_TIMING: KeyframeAnimationOptions = { duration: 1000, iterations: Infinity }
const PULSE_FRAMES: Keyframe[] = [{ opacity: 0.25 }, { opacity: 1 }, { opacity: 0.25 }]
const PULSE_TIMING: KeyframeAnimationOptions = { duration: 1400, iterations: Infinity }
const DOT_TIMING_A: KeyframeAnimationOptions = { duration: 1200, iterations: Infinity, delay: 0 }
const DOT_TIMING_B: KeyframeAnimationOptions = { duration: 1200, iterations: Infinity, delay: 160 }
const DOT_TIMING_C: KeyframeAnimationOptions = { duration: 1200, iterations: Infinity, delay: 320 }

/**
 * A looping animation, through the Web Animations API.
 *
 * THIS EXISTS BECAUSE `@keyframes` NEEDS A STYLESHEET AND THIS FILE HAS NONE. Every
 * other repeating thing in the widget family is drawn on a canvas in a frame loop;
 * these two are a blinking caret and three fading dots, which would be a silly
 * thing to open a `requestAnimationFrame` for. `element.animate()` runs on the
 * compositor, cancels cleanly, and — the reason it beats an interval — is paused by
 * the browser when the tab is hidden without this file having to know that.
 *
 * `enabled` carries `prefers-reduced-motion` from the caller, so a reader who has
 * asked for stillness gets the resting style with nothing moving on top of it.
 */
function useLoop(
  ref: RefObject<HTMLElement | null>,
  frames: Keyframe[],
  timing: KeyframeAnimationOptions,
  enabled: boolean,
): void {
  useEffect(() => {
    const el = ref.current
    // `animate` is everywhere we support, but this is a widget on somebody else's
    // page: a missing method must leave the resting style, not throw.
    if (!enabled || !el || typeof el.animate !== 'function') return
    const anim = el.animate(frames, timing)
    return () => anim.cancel()
  }, [ref, frames, timing, enabled])
}

/**
 * `matchMedia`, not a Tailwind variant, for the file header's reason: inside the
 * embed's shadow root there is no compiled stylesheet for `sm:` to live in.
 */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches,
  )
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia(query)
    setMatches(mq.matches)
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [query])
  return matches
}
